import Axios from 'api/client';
import qs from 'qs';
import { CampaignEvent, SeatingGroup, TableSeatingStats, Ticket, TicketGroup } from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface UpsertEventRequest {
    type: string;
    tickets_required: boolean;
    title: string;
    start_at: string | null;
    end_at: string | null;
    details: string | null;
    private: boolean;
    google_place_id: string | null;
    address_formatted: string | null;
    livestream_start_at?: string | null;
    livestream_end_at?: string | null;
    livestream_url?: string | null;
    livestream_details?: string | null;
    livestream_platform?: string | null;
    livestream_scheduled?: boolean;
    livestream_location?: string | null;
}

type SeatingGroupTicket = Pick<Ticket, 'id' | 'seat_number'>;

interface SeatingGroupRequest {
    id?: number;
    capacity: number;
    number?: number;
    name: string;
    allowed_item_ids?: number[];
    map_data?: object;
    locked?: boolean;
    tickets?: SeatingGroupTicket[];
}

interface AssignSeatingParams {
    seating_groups: SeatingGroupRequest[];
    deleted_seating_groups?: number[];
    unseated_tickets?: number[];
}

interface SeatingGroupAssignResponse {
    seating_groups: SeatingGroup[];
    unseated_tickets: Ticket[];
}

interface FetchAccountEventsParams extends FilterParams {}
interface FetchEventTicketGroupsParams extends FilterParams {}
interface FetchEventSeatingGroupsParams extends FilterParams {}
interface CreateSeatingGroupParams extends Partial<SeatingGroup> {}

const fetchAccountEvents = (accountId: number, params: FetchAccountEventsParams = {}) => {
    const { filters, ...restParams } = params;
    const stringifiedFilters = filters ? qs.stringify(filters) : '';

    return Axios.get<PaginatedData<CampaignEvent>>(
        `/accounts/${accountId}/campaigns/events?${stringifiedFilters}`,
        {
            params: restParams,
        },
    ).then(response => response.data);
};

const fetchEvent = (campaignId: number) =>
    Axios.get<CampaignEvent>('/events', { params: { campaign_id: campaignId } }).then(
        response => response.data,
    );

const upsertEvent = (campaignId: number, data: UpsertEventRequest) =>
    Axios.post('/events', data, { params: { campaign_id: campaignId } }).then(
        response => response.data,
    );

const fetchEventTicketGroups = (eventId: number, params: FetchEventTicketGroupsParams = {}) => {
    return Axios.get<PaginatedData<TicketGroup>>(`/events/${eventId}/ticket_groups`, {
        params,
    }).then(response => response.data);
};

const fetchSeatingGroup = (eventId: number, seatingGroupId: number) =>
    Axios.get<SeatingGroup>(`/events/${eventId}/seating_groups/${seatingGroupId}`).then(
        response => response.data,
    );

const fetchSeatingGroups = (eventId: number, params: FetchEventSeatingGroupsParams) =>
    Axios.get<PaginatedData<SeatingGroup>>(`/events/${eventId}/seating_groups`, {
        params,
    }).then(response => response.data);

const fetchTableSeatingStats = (eventId: number) =>
    Axios.get<TableSeatingStats>(`/events/${eventId}/statistics`).then(response => response.data);

const fetchSeatingLayout = (eventId: number) =>
    Axios.get<{ seating_groups: SeatingGroup[]; unseated_tickets: Ticket[] }>(
        `/events/${eventId}/seating_groups/layout`,
    ).then(response => response.data);

const createSeatingGroup = (eventId: number, params: CreateSeatingGroupParams) =>
    Axios.post<SeatingGroup>(`/events/${eventId}/seating_groups`, params).then(
        response => response.data,
    );

const updateSeatingArrangement = (eventId: number, params: AssignSeatingParams) =>
    Axios.post<SeatingGroupAssignResponse>(`/events/${eventId}/seating_groups/assign`, params).then(
        response => response.data,
    );

const deleteSeatingGroup = (eventId: number, seatingGroupId: number) =>
    Axios.delete(`/events/${eventId}/seating_groups/${seatingGroupId}`).then(
        response => response.data,
    );

const toggleLivestream = (eventId: number) =>
    Axios.post<CampaignEvent>(`/events/${eventId}/toggle_livestream`).then(
        response => response.data,
    );

export type {
    UpsertEventRequest,
    AssignSeatingParams,
    FetchAccountEventsParams,
    FetchEventTicketGroupsParams,
    FetchEventSeatingGroupsParams,
    CreateSeatingGroupParams,
};
export {
    fetchEvent,
    upsertEvent,
    toggleLivestream,
    fetchAccountEvents,
    fetchEventTicketGroups,
    fetchSeatingGroup,
    fetchSeatingGroups,
    fetchTableSeatingStats,
    fetchSeatingLayout,
    createSeatingGroup,
    updateSeatingArrangement,
    deleteSeatingGroup,
};
